import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { AdminListShell } from "@/components/admin/AdminListShell";
import { StatusPill } from "@/components/portal/StatusPill";
import { PlanBadge } from "@/components/admin/PlanBadge";
import { adminOrdersQuery } from "@/lib/query-registry";
import { INVALIDATE } from "@/lib/query-registry";
import { useAuthedMutation } from "@/lib/authed-mutation";
import { retryAudit } from "@/lib/orders.functions";
import type { AdminOrder } from "@/lib/orders.functions";

export const Route = createFileRoute("/admin/orders/")({
  component: AdminOrdersPage,
});

const STATUSES = [
  "all",
  "pending_payment",
  "pending",
  "in_progress",
  "processing",
  "review_needed",
  "ready",
  "complete",
  "failed",
  "cancelled",
] as const;

type StatusFilter = (typeof STATUSES)[number];
type PlanFilter = "all" | "starter" | "pro" | "agency";

function formatPrice(cents: number, currency: string) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
    maximumFractionDigits: 0,
  }).format(cents / 100);
}

function formatDate(iso: string | null) {
  if (!iso) return ", ";
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "2-digit",
  });
}

function AdminOrdersPage() {
  const q = useQuery(adminOrdersQuery());
  const [filter, setFilter] = useState<StatusFilter>("all");
  const [planFilter, setPlanFilter] = useState<PlanFilter>("all");
  const [search, setSearch] = useState("");

  const orders = useMemo(
    () => (q.data as { orders?: AdminOrder[] } | undefined)?.orders ?? [],
    [q.data],
  );

  const retryM = useAuthedMutation(retryAudit, {
    invalidate: [INVALIDATE.adminOrders],
    onSuccess: () => toast.success("Retry triggered."),
    onError: (e) => toast.error(e.message),
  });

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return orders.filter((o) => {
      if (filter !== "all" && o.status !== filter) return false;
      if (planFilter !== "all" && !o.plan_id.toLowerCase().includes(planFilter)) return false;
      if (!term) return true;
      return (
        o.order_number.toLowerCase().includes(term) ||
        o.email.toLowerCase().includes(term) ||
        o.website_url.toLowerCase().includes(term) ||
        o.full_name.toLowerCase().includes(term)
      );
    });
  }, [orders, filter, planFilter, search]);

  const counts = useMemo(() => {
    const map: Record<string, number> = { all: orders.length };
    for (const o of orders) map[o.status] = (map[o.status] ?? 0) + 1;
    return map;
  }, [orders]);

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-semibold text-foreground">Orders</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            ShugertGEO audit orders. Update status and paste the report URL when ready.
          </p>
        </div>
        <input
          type="search"
          placeholder="Search by # / email / website"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full rounded-full border border-border bg-background px-4 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-foreground focus:outline-none sm:w-80"
        />
      </div>

      <div className="mt-6 flex flex-wrap gap-2">
        {STATUSES.map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
              filter === s
                ? "border-foreground bg-foreground text-background"
                : "border-border text-muted-foreground hover:bg-card"
            }`}
          >
            {s.replace(/_/g, " ")}{" "}
            {counts[s] ? <span className="opacity-60">· {counts[s]}</span> : null}
          </button>
        ))}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <span className="text-[10px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
          Plan
        </span>
        {(["all", "starter", "pro", "agency"] as PlanFilter[]).map((p) => (
          <button
            key={p}
            onClick={() => setPlanFilter(p)}
            className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
              planFilter === p
                ? "border-foreground bg-foreground text-background"
                : "border-border text-muted-foreground hover:bg-card"
            }`}
          >
            {p}
          </button>
        ))}
      </div>

      <div className="mt-6 rounded-2xl border border-border bg-card/30">
        <AdminListShell
          query={q}
          isEmpty={() => filtered.length === 0}
          emptyText={orders.length === 0 ? "No orders yet." : "No orders match the current filter."}
        >
          {() => (
            <ul className="divide-y divide-border">
              {filtered.map((o) => (
                <li key={o.id}>
                  <Link
                    to="/admin/orders/$id"
                    params={{ id: o.id }}
                    className="grid items-center gap-3 px-4 py-3 hover:bg-card/60 sm:grid-cols-[110px,1fr,90px,70px,90px,110px,110px]"
                  >
                    <span className="font-mono text-xs text-foreground">{o.order_number}</span>
                    <span className="min-w-0">
                      <span className="block truncate text-sm text-foreground">
                        {o.full_name} · {o.email}
                      </span>
                      <span className="block truncate text-xs text-muted-foreground">
                        {o.website_url}
                      </span>
                    </span>
                    <PlanBadge planId={o.plan_id} planName={o.plan_name} />
                    <span className="font-mono text-xs text-foreground">
                      {o.audit_score != null ? `${o.audit_score}/100` : ", "}
                    </span>
                    <span className="text-xs text-foreground">
                      {formatPrice(o.amount_cents, o.currency)}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {formatDate(o.paid_at ?? o.created_at)}
                    </span>
                    <span
                      className="flex items-center justify-end gap-2"
                      onClick={(e) => {
                        if (o.status !== "failed") return;
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                    >
                      <StatusPill status={o.status} />
                      {o.status === "failed" && (
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (!confirm(`Re-run audit for ${o.website_url}?`)) return;
                            retryM.mutate({ order_id: o.id });
                          }}
                          disabled={retryM.isPending}
                          className="rounded-full bg-destructive px-3 py-1 text-[10px] font-semibold text-destructive-foreground disabled:opacity-40"
                        >
                          Retry
                        </button>
                      )}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </AdminListShell>
      </div>
    </div>
  );
}
